import { graphql } from 'app/generated/gql'

// Category is an ADMIN claim at the engine (api #581/#588): every workflow sends as marketing
// unless a super marks it transactional, and the mutation that does so lives in the OPS schema.
// The builder calls it on save for super users only (see handleSave).
export const SET_WORKFLOW_CATEGORY_MUTATION = graphql(`
  mutation SetWorkflowCategory($id: ID!, $category: MessageCategory!) {
    setWorkflowCategory(id: $id, category: $category) {
      id
      category
    }
  }
`)

export const LIST_WORKFLOWS_QUERY = graphql(`
  query Workflows($search: String, $pagination: PaginationInput, $sort: SortInput) {
    workflows(search: $search, pagination: $pagination, sort: $sort) {
      hits {
        version
        createdDate
        updatedDate
        id
        orgId
        workspaceId
        locationId
        name
        active
        paused
        pausedReason
        pausedNodeId
        pausedDate
        triggerStartDate
        triggerJson
        nodesJson
        edgesJson
        settingsJson
        builderJson
        category
      }
      total
      page
    }
  }
`)

// Recent Form-type activities, used to aggregate a "your forms" selector for the trigger Scope step
// (there's no first-class Forms model yet — see useFormOptions).
export const FORM_ACTIVITIES_QUERY = graphql(`
  query FormActivitiesForPicker($search: String, $pagination: PaginationInput, $sort: SortInput) {
    activities(search: $search, pagination: $pagination, sort: $sort) {
      hits {
        id
        typeId
      }
      total
    }
  }
`)

// Recent activities (with their binder fields + linked contact) for the trigger "use a recent record"
// sample. Form detail (title/answers) is resolved separately from FormJson (see useTriggerSamples).
export const TRIGGER_SAMPLE_ACTIVITIES_QUERY = graphql(`
  query TriggerSampleActivities($search: String, $pagination: PaginationInput, $sort: SortInput) {
    activities(search: $search, pagination: $pagination, sort: $sort) {
      hits {
        id
        typeId
        type
        subType
        activityDate
        contactName
        contactPhone
        source
        vendor
        channel
        trackingNumberName
        tags
        contacts {
          id
          name
        }
      }
    }
  }
`)

export const GET_WORKFLOW_QUERY = graphql(`
  query Workflow($id: ID!) {
    workflow(id: $id) {
      version
      createdDate
      updatedDate
      id
      orgId
      workspaceId
      locationId
      name
      active
      paused
      pausedReason
      pausedNodeId
      pausedDate
      triggerStartDate
      triggerJson
      nodesJson
      edgesJson
      settingsJson
      builderJson
      category
    }
  }
`)

export const CREATE_WORKFLOW_MUTATION = graphql(`
  mutation CreateWorkflow($input: CreateWorkflowInput!) {
    createWorkflow(input: $input) {
      version
      createdDate
      updatedDate
      id
      orgId
      workspaceId
      locationId
      name
      active
      paused
      pausedReason
      pausedNodeId
      pausedDate
      triggerStartDate
      triggerJson
      nodesJson
      edgesJson
      settingsJson
      builderJson
      category
    }
  }
`)

export const UPDATE_WORKFLOW_MUTATION = graphql(`
  mutation UpdateWorkflow($id: ID!, $input: UpdateWorkflowInput!) {
    updateWorkflow(id: $id, input: $input) {
      version
      createdDate
      updatedDate
      id
      orgId
      workspaceId
      locationId
      name
      active
      paused
      pausedReason
      pausedNodeId
      pausedDate
      triggerStartDate
      triggerJson
      nodesJson
      edgesJson
      settingsJson
      builderJson
      category
    }
  }
`)

export const DELETE_WORKFLOW_MUTATION = graphql(`
  mutation DeleteWorkflow($id: ID!) {
    deleteWorkflow(id: $id)
  }
`)

/** Recent events of the trigger's type, for the "Why didn't it fire?" explainer: each event's
 *  `workflowIds` says whether this workflow processed it, and `data` holds the change payload the
 *  entry conditions were matched against — enough to diagnose every stage client-side. */
export const WORKFLOW_TRIGGER_EVENTS_QUERY = graphql(`
  query WorkflowTriggerEvents($search: String, $pagination: PaginationInput, $sort: SortInput) {
    events(search: $search, pagination: $pagination, sort: $sort) {
      hits {
        id
        type
        typeId
        action
        eventDate
        createdDate
        source
        data
        workflowIds
      }
      total
    }
  }
`)

/** The Event doc a run was enrolled by (run.enrollmentEventId → event). Since the event-stream
 *  pivot this is the ONLY provenance on organically-enrolled runs — run.activityId is null. */
export const ENROLLMENT_EVENT_QUERY = graphql(`
  query WorkflowEnrollmentEvent($id: ID!) {
    event(id: $id) {
      id
      type
      typeId
      action
      eventDate
      source
      # A form's vendor, which decides whether eventDate is on the location's clock (isWallClockEvent).
      data
    }
  }
`)

export const LIST_WORKFLOW_RUNS_QUERY = graphql(`
  query WorkflowRuns($search: String, $pagination: PaginationInput, $sort: SortInput) {
    workflowRuns(search: $search, pagination: $pagination, sort: $sort) {
      hits {
        id
        updatedDate
        workflowId
        contactId
        activityId
        status
        currentNodeId
        resumeAt
        waitingFor
        startedAt
        exitReason
        enrollmentEventId
      }
      total
      page
    }
  }
`)

// The builder reads this on every open: its canvas may hydrate from a local copy and never ask for
// the workflow, but whether the engine stopped it is only known to the server.
export const WORKFLOW_STOPPED_QUERY = graphql(`
  query WorkflowStopped($id: ID!, $heldSearch: String) {
    workflow(id: $id) {
      id
      paused
      pausedReason
      pausedNodeId
      pausedDate
    }
    workflowRuns(search: $heldSearch, pagination: { limit: 1 }) {
      total
    }
  }
`)

// Rejection records (TRK-5581, api #599, verified 08-26): the engine writes a run-less
// WorkflowRunEvent with status skipped at each enrollment decline — workflowId and eventId are
// top-level index fields, outputJson carries {reason}. This is what turns the explainer's
// reconstruction into fact.
export const WORKFLOW_REJECTIONS_QUERY = graphql(`
  query WorkflowRejections($search: String, $pagination: PaginationInput, $sort: SortInput) {
    workflowRunEvents(search: $search, pagination: $pagination, sort: $sort) {
      hits {
        id
        eventId
        contactId
        outputJson
        createdDate
      }
      total
    }
  }
`)

export const WORKFLOW_RUN_EVENTS_QUERY = graphql(`
  query WorkflowRunEvents($search: String, $pagination: PaginationInput, $sort: SortInput) {
    workflowRunEvents(search: $search, pagination: $pagination, sort: $sort) {
      hits {
        id
        runId
        nodeId
        nodeType
        status
        timestamp
        outputJson
      }
      total
      page
    }
  }
`)

export const CANCEL_WORKFLOW_RUN_MUTATION = graphql(`
  mutation CancelWorkflowRun($id: ID!) {
    cancelWorkflowRun(id: $id) {
      id
      status
      exitReason
    }
  }
`)

export const TRIGGER_WORKFLOW_ON_CONTACT_MUTATION = graphql(`
  mutation TriggerWorkflowOnContact($workflowId: ID!, $contactId: ID!) {
    triggerWorkflowOnContact(workflowId: $workflowId, contactId: $contactId) {
      success
      workflowType
      workflowId
      contactId
    }
  }
`)

/** Contact search for the Runs panel's "Test on a contact" picker (UX audit C4): nobody outside
 *  this room has ever seen a raw contact id, so the panel searches by name instead. The run list
 *  reuses it to show each run's contact by name. */
export const WORKFLOW_TEST_CONTACTS_QUERY = graphql(`
  query WorkflowTestContacts($search: String, $pagination: PaginationInput) {
    contacts(search: $search, pagination: $pagination) {
      hits {
        id
        name
        cellPhone
      }
      total
    }
  }
`)

/** The workspace-wide send caps (TRK-5385) the engine enforces, for the header pill, the settings
 *  modal and the activation preflight. */
export const WORKSPACE_THROTTLE_QUERY = graphql(`
  query WorkspaceThrottle($id: ID!) {
    workspace(id: $id) {
      id
      maxOutboundMonthly
      maxOutboundDaily
      maxEnrollmentsPerHour
    }
  }
`)

/** The workspace's locations, for the builder's location picker. A workflow belongs to exactly one
 *  (trackable-api #530): its number sends the texts and its timezone decides quiet hours. */
export const WORKFLOW_LOCATIONS_QUERY = graphql(`
  query WorkflowLocations($pagination: PaginationInput) {
    locations(pagination: $pagination) {
      hits {
        id
        name
        # The Wait card's business-hours hold is a no-op without both of these, so the builder
        # reads them to decide whether that caveat still applies (validation.locationHasBusinessHours).
        timezone
        openHours {
          sunday {
            open
            hours {
              from
              to
            }
          }
          monday {
            open
            hours {
              from
              to
            }
          }
          tuesday {
            open
            hours {
              from
              to
            }
          }
          wednesday {
            open
            hours {
              from
              to
            }
          }
          thursday {
            open
            hours {
              from
              to
            }
          }
          friday {
            open
            hours {
              from
              to
            }
          }
          saturday {
            open
            hours {
              from
              to
            }
          }
        }
      }
      total
    }
  }
`)

/** Contacts behind a page of lookback matches, by the contact ids the engine resolved. */
export const LOOKBACK_CONTACTS_QUERY = graphql(`
  query WorkflowLookbackContacts($search: String, $pagination: PaginationInput) {
    contacts(search: $search, pagination: $pagination) {
      hits {
        id
        name
        archived
        pipelines {
          id
          stage
          key
        }
      }
      total
    }
  }
`)

/** Today's state of the appointments a lookback matched, so a stale match is visible as stale. */
export const LOOKBACK_APPOINTMENTS_QUERY = graphql(`
  query WorkflowLookbackAppointments($search: String, $pagination: PaginationInput) {
    appointments(search: $search, pagination: $pagination) {
      hits {
        id
        type
        status
        date
        time
      }
      total
    }
  }
`)

/** The engine's own answer to "who would this trigger have fired for" (TRK-5826): the same matcher
 *  the evaluator runs, over a past window, for a saved or unsaved trigger. Reads only. */
export const PREVIEW_WORKFLOW_TRIGGER_QUERY = graphql(`
  query PreviewWorkflowTrigger(
    $trigger: WorkflowTriggerInput!
    $locationId: ID!
    $windowStart: DateTime!
    $windowEnd: DateTime!
    $pagination: PaginationInput
  ) {
    previewWorkflowTrigger(
      trigger: $trigger
      locationId: $locationId
      windowStart: $windowStart
      windowEnd: $windowEnd
      pagination: $pagination
    ) {
      matches {
        sourceType
        sourceId
        eventId
        contactId
        date
        conditionValues
        reconstructedFromRecord
      }
      total
      page
      examinedCount
      conditionRejections {
        attribute
        operator
        value
        rejectedCount
      }
      eventHistoryStart
    }
  }
`)
